// src/components/Onboarding/canvas/animationDrawing.js
// Animation drawing functions for canvas overlays

/**
 * Draw animation - Industry standard animation rendering
 * Supports various animation types that can be easily extended
 */
export function drawAnimation(ctx, animationType, width, height, subtype) {
  const now = performance.now();
  
  switch (animationType) {
    case 'pulse': {
      // Pulsing glow effect - perfect for body animation
      const period = 1800; // 1.8 seconds per pulse
      const phase = (now % period) / period;
      
      // Sine wave for smooth pulsing
      const intensity = (Math.sin(phase * Math.PI * 2) + 1) / 2;
      
      // Create gradient based on intensity
      const gradient = ctx.createRadialGradient(
        width / 2, height / 2, 0,
        width / 2, height / 2, Math.max(width, height) / 2
      );
      
      const alpha = 0.3 + intensity * 0.4; // Pulse between 30% and 70% opacity
      gradient.addColorStop(0, `rgba(255, 255, 255, ${alpha})`);
      gradient.addColorStop(0.5, `rgba(200, 220, 255, ${alpha * 0.6})`);
      gradient.addColorStop(1, `rgba(100, 150, 255, 0)`);
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
      break;
    }
    
    case 'glow': {
      // Subtle glowing effect - perfect for hair animation
      const period = 2000; // 2 seconds per glow cycle
      const phase = (now % period) / period;
      
      // Ease in-out for smooth glow
      const eased = phase < 0.5 
        ? 2 * phase * phase 
        : 1 - Math.pow(-2 * phase + 2, 2) / 2;
      
      const intensity = eased;
      
      // Soft blue glow for hair
      const gradient = ctx.createLinearGradient(0, 0, 0, height);
      
      const alpha = 0.2 + intensity * 0.5; // Glow between 20% and 70% opacity
      gradient.addColorStop(0, `rgba(59, 130, 246, ${alpha})`); // Blue-500
      gradient.addColorStop(0.5, `rgba(96, 165, 250, ${alpha * 0.8})`); // Blue-400
      gradient.addColorStop(1, `rgba(147, 197, 253, ${alpha * 0.6})`); // Blue-300
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
      break;
    }
    
    case 'bodySweepSequence': {
      // Sophisticated body detection light sweep sequence
      // This is called from the animation overlay which passes the config
      // We need to access the overlay config through a parameter
      // For now, we'll handle this as a special case
      break; // Handled separately in drawOverlay
    }
    
    case 'sweep': {
      // Light sweep effect - can be used for either
      const period = 2500; // 2.5 seconds per sweep
      const phase = (now % period) / period;
      
      // Position of sweep
      const sweepX = -width + (phase * width * 2);
      
      // Create gradient for sweep
      const gradient = ctx.createLinearGradient(sweepX, 0, sweepX + width * 0.3, 0);
      gradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
      gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.6)');
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
      break;
    }
    
    case 'shimmer': {
      // Shimmering particles effect
      const period = 1500;
      const phase = (now % period) / period;
      
      // Multiple waves
      for (let i = 0; i < 3; i++) {
        const offset = (i / 3);
        const wavePhase = (phase + offset) % 1;
        const y = height * wavePhase;
        
        const gradient = ctx.createLinearGradient(0, y - 20, 0, y + 20);
        gradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
        gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.4)');
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
        
        ctx.fillStyle = gradient;
        ctx.fillRect(0, Math.max(0, y - 20), width, 40);
      }
      break;
    }
    
    default:
      // Fallback: simple pulse
      const period = 1500;
      const phase = (now % period) / period;
      const alpha = 0.2 + Math.sin(phase * Math.PI * 2) * 0.2;
      ctx.fillStyle = `rgba(100, 150, 255, ${alpha})`;
      ctx.fillRect(0, 0, width, height);
  }
}

/**
 * Draw body sweep sequence - Light sweep animation for body detection
 * Two back-to-back sweeps across the whole canvas
 */
export function drawBodySweepSequence(mainCtx, tempCtx, maskImg, config, fit, srcMaskX, srcMaskY, srcMaskW, srcMaskH) {
  const now = performance.now();
  const { sweeps, sweepAngle, totalDuration, loop, startTime } = config;
  
  // If no start time, don't draw
  if (!startTime) return;
  
  // Calculate elapsed time since animation started
  const elapsed = now - startTime;
  
  // Calculate animation time - either looping or single pass
  const animTime = loop ? (elapsed % totalDuration) : Math.min(elapsed, totalDuration);
  
  // If animation is complete and not looping, don't draw anything
  if (!loop && elapsed >= totalDuration) {
    return;
  }
  
  // Convert angle to radians
  const angleRad = (sweepAngle * Math.PI) / 180;
  
  // Create canvas for drawing sweeps
  const sweepCanvas = document.createElement('canvas');
  sweepCanvas.width = fit.width;
  sweepCanvas.height = fit.height;
  const sweepCtx = sweepCanvas.getContext('2d');
  
  // Draw each sweep
  sweeps.forEach(sweepConfig => {
    // Check if this sweep should be active
    if (animTime < sweepConfig.delay) return; // Not started yet
    
    const sweepTime = animTime - sweepConfig.delay;
    if (sweepTime > sweepConfig.speed) return; // Already finished
    
    // Progress of this sweep (0 to 1)
    const progress = sweepTime / sweepConfig.speed;
    
    // Smooth easing for natural movement
    const eased = progress < 0.5 
      ? 2 * progress * progress 
      : 1 - Math.pow(-2 * progress + 2, 2) / 2;
    
    // Calculate sweep position (moves from left to right)
    const startX = -fit.width * 0.2;
    const endX = fit.width * 1.2;
    const sweepX = startX + eased * (endX - startX);
    
    // Sweep width in pixels
    const sweepWidthPx = fit.width * sweepConfig.width;
    
    // Create angled gradient for the light sweep
    const angle = angleRad;
    const tanAngle = Math.tan(angle);
    const offsetY = tanAngle * sweepWidthPx;
    
    const gradient = sweepCtx.createLinearGradient(
      sweepX - sweepWidthPx / 2, 0,
      sweepX + sweepWidthPx / 2, 0
    );
    
    // Realistic light sweep gradient
    gradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
    gradient.addColorStop(0.1, `rgba(255, 255, 255, ${sweepConfig.opacity * 0.2})`);
    gradient.addColorStop(0.5, `rgba(255, 255, 255, ${sweepConfig.opacity})`);
    gradient.addColorStop(0.9, `rgba(255, 255, 255, ${sweepConfig.opacity * 0.2})`);
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
    
    sweepCtx.fillStyle = gradient;
    
    // Draw angled sweep
    sweepCtx.save();
    sweepCtx.beginPath();
    
    // Create a rotated rectangle path
    const hw = sweepWidthPx / 2;
    const hh = fit.height;
    
    sweepCtx.moveTo(sweepX - hw, -offsetY);
    sweepCtx.lineTo(sweepX + hw, offsetY);
    sweepCtx.lineTo(sweepX + hw, hh + offsetY);
    sweepCtx.lineTo(sweepX - hw, hh - offsetY);
    sweepCtx.closePath();
    
    sweepCtx.fill();
    sweepCtx.restore();
  });
  
  // Draw the sweep canvas to main canvas
  mainCtx.drawImage(sweepCanvas, fit.x, fit.y);
}
